import { Injectable } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class StorageService {
  get<T>(key: string): T | null {
    try {
      const data = sessionStorage.getItem(key);
      return data ? (JSON.parse(data) as T) : null;
    } catch {
      return null;
    }
  }

  set<T>(key: string, value: T): void {
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
    } catch {
      // Persisting a preference is best-effort, as with setLocal.
    }
  }

  update<T extends PlainObject>(key: string, partial: Partial<T>): void {
    const current = this.get<unknown>(key);

    if (current != null && !isPlainObject(current)) {
      console.warn(
        `StorageService.update: key "${key}" is not a plain object. Overwriting with a new object.`
      );
      this.set(key, partial as T);
      return;
    }

    const base = (current ?? {}) as T;
    const updated = { ...base, ...partial } as T;
    this.set(key, updated);
  }

  remove(key: string): void {
    try {
      sessionStorage.removeItem(key);
    } catch {
      // Storage may be blocked by browser policy.
    }
  }

  /**
   * localStorage twin of `get`, for preferences that outlive the tab. Wrapped
   * because localStorage throws in private mode and partitioned iframes.
   */
  getLocal<T>(key: string): T | null {
    try {
      const data = localStorage.getItem(key);
      return data ? (JSON.parse(data) as T) : null;
    } catch {
      return null;
    }
  }

  setLocal<T>(key: string, value: T): void {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // Persisting a preference is best-effort; failing must not break callers.
    }
  }
}

type PlainObject = Record<string, unknown>;

function isPlainObject(v: unknown): v is PlainObject {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}
